/**
 * ContentTag Service
 *
 * Domain layer service for content tag operations.
 * Singleton pattern matching other service singletons.
 *
 * Architecture:
 * UseCase → ContentTagService → FirestoreService
 */

import type { PageCursor } from '@/data/types/json';
import { firestoreService } from '@/data/services/firestoreService';
import { ContentTagRemapper } from '@/domain/remappers/contentTag/contentTagRemapper';
import type { ContentTagEntity } from '@/domain/entities/contentTag/contentTagEntity';
import { UserMediaRemapper } from '@/domain/remappers/userMedia/userMediaRemapper';
import type { UserMediaDto } from '@/data/dtos/userMedia/userMediaDto';

export interface ContentTagSearchResult {
  contentTags: ContentTagEntity[];
  hasMore: boolean;
  cursor?: PageCursor;
}

export class ContentTagService {
  private static instance: ContentTagService;

  private constructor() { }

  public static getInstance(): ContentTagService {
    if (!ContentTagService.instance) {
      ContentTagService.instance = new ContentTagService();
    }
    return ContentTagService.instance;
  }

  /**
   * Search content tags by prefix
   * Matches Flutter's ContentTagsRepository.getContentTags
   */
  async searchContentTags(
    prefix: string,
    cursor?: PageCursor,
    limitCount: number = 8
  ): Promise<ContentTagSearchResult> {
    const result = await firestoreService.searchContentTags(
      prefix.toLowerCase(),
      cursor,
      limitCount
    );

    const contentTags = ContentTagRemapper.fromJsonList(result.items)
      .map(ContentTagRemapper.toEntity);

    return {
      contentTags,
      hasMore: result.hasMore,
      cursor: result.cursor,
    };
  }

  /**
   * Get content (media) by hashtag or content tag
   * Matches Flutter's MediasRepository.getContentByHashTag
   */
  async getContentByTag(
    tagName: string,
    isHashtag: boolean,
    cursor?: PageCursor,
    limitCount?: number
  ): Promise<{ items: UserMediaDto[]; hasMore: boolean; cursor?: PageCursor }> {
    const result = await firestoreService.getContentByTag(tagName, isHashtag, cursor, limitCount);

    return {
      items: UserMediaRemapper.fromJsonList(result.items),
      hasMore: result.hasMore,
      cursor: result.cursor,
    };
  }
}

export const contentTagService = ContentTagService.getInstance();
